/** Client-side upload guards — backend must enforce the same limits. */

export const DEFAULT_MAX_IMAGE_BYTES = 5 * 1024 * 1024 // 5 MB
export const DEFAULT_MAX_CSV_BYTES = 10 * 1024 * 1024 // 10 MB

const IMAGE_MIME_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
])

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp)$/i

/**
 * @param {File} file
 * @param {{ maxBytes?: number, allowedMimeTypes?: Set<string> }} [options]
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateImageFile(file, options = {}) {
    if (!file) {
        return { ok: false, message: 'No file selected.' }
    }

    const maxBytes = options.maxBytes ?? DEFAULT_MAX_IMAGE_BYTES
    if (file.size > maxBytes) {
        const mb = Math.round(maxBytes / (1024 * 1024))
        return { ok: false, message: `Image must be ${mb} MB or smaller.` }
    }

    const allowed = options.allowedMimeTypes ?? IMAGE_MIME_TYPES
    const mimeOk = file.type && allowed.has(file.type)
    const extOk = IMAGE_EXTENSIONS.test(file.name || '')

    if (!mimeOk && !extOk) {
        return {
            ok: false,
            message: 'Please upload a JPEG, PNG, GIF, or WebP image.',
        }
    }

    return { ok: true }
}

/**
 * @param {File} file
 * @param {{ maxBytes?: number }} [options]
 */
export function validateCsvFile(file, options = {}) {
    if (!file) {
        return { ok: false, message: 'No file selected.' }
    }

    const maxBytes = options.maxBytes ?? DEFAULT_MAX_CSV_BYTES
    if (file.size > maxBytes) {
        const mb = Math.round(maxBytes / (1024 * 1024))
        return { ok: false, message: `CSV must be ${mb} MB or smaller.` }
    }

    const name = (file.name || '').toLowerCase()
    const typeOk =
        file.type === 'text/csv' ||
        file.type === 'application/vnd.ms-excel' ||
        name.endsWith('.csv')

    if (!typeOk) {
        return { ok: false, message: 'Please upload a .csv file.' }
    }

    return { ok: true }
}
